// src/utils/tooltipHelpers.js
// Tooltip management utilities extracted from UnitViewer.js
// UPDATED to handle both old (.story-tooltip) and new (.has-callout) callouts

/**
 * Show tooltip at specified element position
 */
export const showTooltip = (element, content) => {
    hideTooltip(); // Remove any existing tooltip
    
    const tooltip = document.createElement('div');
    tooltip.className = 'story-tooltip-popup';
    
    // Handle both HTML content and plain text
    if (content.includes('<') && content.includes('>')) {
        // HTML content (for image callouts)
        tooltip.innerHTML = content;
    } else {
        // Plain text content
        tooltip.textContent = content;
    }
    
    tooltip.style.opacity = '1';
    tooltip.style.pointerEvents = 'auto';
    document.body.appendChild(tooltip);
    
    // Position the tooltip
    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    
    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
    let top = rect.top - tooltipRect.height - 10;
    
    // Adjust if tooltip would go off screen
    if (left < 10) left = 10;
    if (left + tooltipRect.width > window.innerWidth - 10) {
        left = window.innerWidth - tooltipRect.width - 10;
    }
    
    // If tooltip would go above viewport, show below instead
    if (top < 10) {
        top = rect.bottom + 10;
        tooltip.classList.add('below');
    }
    
    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
};

/**
 * Hide any existing tooltips
 */
export const hideTooltip = () => {
    const existingTooltip = document.querySelector('.story-tooltip-popup');
    if (existingTooltip) {
        existingTooltip.remove();
    }
};

/**
 * Get tooltip content from element - handles both old and new formats
 */
const getTooltipContent = (element) => {
    // Check for old format first
    if (element.hasAttribute('data-tooltip')) {
        return element.getAttribute('data-tooltip');
    }
    
    // Check for new callout format
    if (element.classList.contains('has-callout')) {
        // Check various possible attribute names
        return element.getAttribute('data-callout') || 
               element.getAttribute('data-content') || 
               element.getAttribute('title');
    }
    
    return null;
};

/**
 * Check if element is a tooltip/callout element
 */
const isTooltipElement = (element) => {
    return element.classList.contains('story-tooltip') || 
           element.classList.contains('has-callout');
};

/**
 * Create tooltip event handlers
 */
export const createTooltipHandlers = () => {
    const handleTooltipMouseOver = (e) => {
        if (isTooltipElement(e.target)) {
            const tooltipContent = getTooltipContent(e.target);
            if (tooltipContent) {
                showTooltip(e.target, tooltipContent);
            }
        }
    };

    const handleTooltipMouseOut = (e) => {
        if (isTooltipElement(e.target)) {
            hideTooltip();
        }
    };

    const handleTooltipClick = (e) => {
        // For mobile devices - toggle tooltip on click
        if (isTooltipElement(e.target)) {
            e.preventDefault();
            const tooltipContent = getTooltipContent(e.target);
            if (tooltipContent) {
                const existingTooltip = document.querySelector('.story-tooltip-popup');
                if (existingTooltip) {
                    hideTooltip();
                } else {
                    showTooltip(e.target, tooltipContent);
                    // Auto-hide after 4 seconds on mobile
                    setTimeout(hideTooltip, 4000);
                }
            }
        }
    };

    return {
        onMouseOver: handleTooltipMouseOver,
        onMouseOut: handleTooltipMouseOut,
        onClick: handleTooltipClick
    };
};

/**
 * Initialize tooltips for a container element
 * Call this after content is loaded/updated
 */
export const initializeTooltips = (container = document) => {
    // Remove existing listeners to avoid duplicates
    cleanupTooltips();
    
    const handlers = createTooltipHandlers();
    
    // Add event listeners to the container
    container.addEventListener('mouseover', handlers.onMouseOver);
    container.addEventListener('mouseout', handlers.onMouseOut);
    container.addEventListener('click', handlers.onClick);
    
    console.log('Tooltips initialized for', container);
};

/**
 * Cleanup tooltips on component unmount
 */
export const cleanupTooltips = () => {
    hideTooltip();
    
    // Remove event listeners if needed
    const handlers = createTooltipHandlers();
    document.removeEventListener('mouseover', handlers.onMouseOver);
    document.removeEventListener('mouseout', handlers.onMouseOut);
    document.removeEventListener('click', handlers.onClick);
};